/**
 * Auswahl der Animation für einen Arbeitsschritt: Vorschläge aus dem
 * Schritttext, Galerie aller Vorlagen, Einstellungen und Vorschau.
 */
import {
  findTemplate,
  frameSvg,
  suggestTemplates,
  templatesByGroup,
  withDefaults,
} from '@cookbook/animations';
import type { AnimationTemplate, ParameterValue, TemplateParameter } from '@cookbook/animations';
import { memo, useId, useMemo, useState } from 'react';
import { useAnimationPalette } from '../lib/animation.ts';
import { texts } from '../texts/de.ts';
import { StepAnimation } from './StepAnimation.tsx';

/** Werte einer Animation am Schritt. */
export type AnimationParams = Record<string, ParameterValue>;

/** Eigenschaften der Auswahl. */
export interface AnimationPickerProps {
  /** Titel und Anleitung des Schritts, daraus entstehen die Vorschläge. */
  readonly text: string;
  readonly templateId: string | null;
  readonly params: AnimationParams;
  readonly onChange: (templateId: string | null, params: AnimationParams) => void;
}

/** Kleines Standbild einer Vorlage für Galerie und Vorschläge. */
const Thumbnail = memo(function Thumbnail({
  template,
  values,
}: {
  readonly template: AnimationTemplate;
  readonly values: AnimationParams;
}) {
  const palette = useAnimationPalette();
  // Eigene Vorsilbe je Bild, damit Beschnittpfade auf der Seite eindeutig bleiben.
  const idPrefix = `thumb${useId().replace(/[^a-zA-Z0-9]/g, '')}`;
  const valuesKey = JSON.stringify(values);
  const svg = useMemo(
    () =>
      frameSvg(template, JSON.parse(valuesKey) as AnimationParams, template.stillTime, { palette, idPrefix }),
    [template, valuesKey, palette, idPrefix],
  );
  // Das SVG entsteht vollständig in der eigenen Bibliothek; Texte darin sind maskiert.
  return <span className="block overflow-hidden rounded-lg" dangerouslySetInnerHTML={{ __html: svg }} />;
});

/** Steuerelement für einen Parameter. */
function ParameterControl({
  parameter,
  value,
  onChange,
}: {
  readonly parameter: TemplateParameter;
  readonly value: ParameterValue;
  readonly onChange: (value: ParameterValue) => void;
}) {
  if (parameter.kind === 'choice') {
    return (
      <label className="flex items-center gap-2">
        <span className="text-content-muted">{parameter.label}</span>
        <select
          value={String(value)}
          onChange={(event) => onChange(event.target.value)}
          className="rounded-lg border border-border-subtle bg-surface px-2 py-1"
        >
          {parameter.options.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
      </label>
    );
  }
  if (parameter.kind === 'number') {
    return (
      <label className="flex items-center gap-2">
        <span className="text-content-muted">{parameter.label}</span>
        <input
          type="number"
          min={parameter.min}
          max={parameter.max}
          step={parameter.step ?? 1}
          value={Number(value)}
          onChange={(event) => onChange(Number(event.target.value))}
          className="w-20 rounded-lg border border-border-subtle bg-surface px-2 py-1"
        />
        {parameter.unit === undefined ? null : <span className="text-content-muted">{parameter.unit}</span>}
      </label>
    );
  }
  return (
    <label className="flex items-center gap-2">
      <input type="checkbox" checked={value === true} onChange={(event) => onChange(event.target.checked)} />
      {parameter.label}
    </label>
  );
}

/** Auswahl der Animation eines Schritts. */
export function AnimationPicker({ text, templateId, params, onChange }: AnimationPickerProps) {
  const template = findTemplate(templateId);
  const [galleryOpen, setGalleryOpen] = useState(false);
  const suggestions = useMemo(() => suggestTemplates(text, 4), [text]);
  const groups = useMemo(() => templatesByGroup().filter((group) => group.templates.length > 0), []);
  const values = template === undefined ? {} : withDefaults(template, params);

  /** Übernimmt eine Vorlage mit Startwerten. */
  function choose(next: AnimationTemplate, startValues: AnimationParams = {}) {
    onChange(next.id, { ...withDefaults(next, startValues) });
    setGalleryOpen(false);
  }

  return (
    <div className="space-y-3 text-sm">
      {template === undefined ? (
        <p className="text-content-muted">{texts.animation.none}</p>
      ) : (
        <div className="flex flex-col gap-3 sm:flex-row sm:items-start">
          <StepAnimation templateId={template.id} params={values} className="w-full sm:w-64" />
          <div className="min-w-0 flex-1 space-y-2">
            <p className="font-semibold">{template.title}</p>
            <p className="text-xs text-content-muted">{template.description}</p>
            {template.parameters.length === 0 ? null : (
              <fieldset className="flex flex-wrap gap-3">
                <legend className="sr-only">{texts.animation.settings}</legend>
                {template.parameters.map((parameter) => (
                  <ParameterControl
                    key={parameter.name}
                    parameter={parameter}
                    value={values[parameter.name] ?? parameter.defaultValue}
                    onChange={(value) => onChange(template.id, { ...values, [parameter.name]: value })}
                  />
                ))}
              </fieldset>
            )}
            <button
              type="button"
              onClick={() => onChange(null, {})}
              className="rounded-lg border border-border-subtle px-2 py-1 text-xs text-content-muted"
            >
              {texts.animation.remove}
            </button>
          </div>
        </div>
      )}

      <div>
        <p className="mb-1 text-xs font-semibold text-content-muted">{texts.animation.suggestions}</p>
        {suggestions.length === 0 ? (
          <p className="text-xs text-content-muted">{texts.animation.noSuggestions}</p>
        ) : (
          <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
            {suggestions.map((suggestion) => (
              <button
                key={suggestion.template.id}
                type="button"
                onClick={() => choose(suggestion.template, suggestion.values)}
                aria-pressed={suggestion.template.id === templateId}
                className={`rounded-xl border p-1.5 text-left text-xs ${
                  suggestion.template.id === templateId ? 'border-accent' : 'border-border-subtle'
                }`}
              >
                <Thumbnail
                  template={suggestion.template}
                  values={withDefaults(suggestion.template, suggestion.values)}
                />
                <span className="mt-1 block font-medium">{suggestion.template.title}</span>
              </button>
            ))}
          </div>
        )}
      </div>

      <div>
        <button
          type="button"
          onClick={() => setGalleryOpen((value) => !value)}
          aria-expanded={galleryOpen}
          className="rounded-lg border border-border-subtle px-3 py-1.5 text-xs font-medium"
        >
          {galleryOpen
            ? texts.common.close
            : template === undefined
              ? texts.animation.gallery
              : texts.animation.change}
        </button>
        {galleryOpen ? (
          <div className="mt-2 space-y-3">
            {groups.map((group) => (
              <section key={group.group}>
                <h4 className="mb-1 text-xs font-semibold text-content-muted">{group.label}</h4>
                <div className="grid grid-cols-3 gap-2 sm:grid-cols-5">
                  {group.templates.map((entry) => (
                    <button
                      key={entry.id}
                      type="button"
                      onClick={() => choose(entry)}
                      aria-pressed={entry.id === templateId}
                      className={`rounded-xl border p-1 text-left text-xs ${
                        entry.id === templateId ? 'border-accent' : 'border-border-subtle'
                      }`}
                    >
                      <Thumbnail template={entry} values={withDefaults(entry, {})} />
                      <span className="mt-1 block leading-tight">{entry.title}</span>
                    </button>
                  ))}
                </div>
              </section>
            ))}
          </div>
        ) : null}
      </div>
    </div>
  );
}
